import { Component, OnInit, OnDestroy, signal, computed, effect, inject, input, booleanAttribute } from '@angular/core'
import { CommonModule } from '@angular/common'
import { ActivatedRoute } from '@angular/router'
import { Subscription, interval } from 'rxjs'
import { MessageService } from '@openng/optimus-ui/api'
import { OptimUiSharedModule } from '../../../shared/optimus-ui-shared.module'
import { AwpMediationLayerApiService, AwpMediationWorkflowApiService } from '../../../_services'
import type { WorkflowRunCounts } from '../../../_services'
import type { MediationTag } from '../../../_services/AWP/mediationLayer.service'
import { WorkflowGraphComponent, WorkflowGraphNode } from '../workflow/workflow-graph.component'
import { actionOutputs, setActionCatalog } from '../workflow/workflow-catalog'
import { formatDateTime, formatTime } from '../workflow/workflow-format'
import type { WorkflowRun, WorkflowRunActionOutcome, WorkflowRunActionPayload, WorkflowRunResult, WorkflowRunStepResult } from '../workflow/workflow.model'

export type RunFilterKey = 'all' | 'active' | 'scheduled' | 'waiting' | 'completed' | 'failed'

const EMPTY_COUNTS: WorkflowRunCounts = {
  all: 0,
  active: 0,
  scheduled: 0,
  completed: 0,
  failed: 0,
  waiting: 0,
  cancelled: 0
}

interface FrazDrillRow {
  fraz: string
  status: string
  steps: WorkflowRunStepResult[]
  successCount: number
  failedCount: number
  skippedCount: number
  addedTags: number[]
  removedTags: number[]
  durationMs: number
  /** Motivo del fallimento (primo step fallito con messaggio). */
  error: string | null
  failedStepLabel: string | null
}

@Component({
  selector: 'app-mediation-runs',
  standalone: true,
  imports: [CommonModule, OptimUiSharedModule, WorkflowGraphComponent],
  templateUrl: './mediation-runs.component.html',
  styleUrls: ['./mediation-runs.component.scss'],
  host: { '(document:keydown.escape)': 'onDocumentEscapeKey()' }
})
export class MediationRunsComponent implements OnInit, OnDestroy {
  private readonly messageService = inject(MessageService)
  private readonly mediationWorkflowApi = inject(AwpMediationWorkflowApiService)
  private readonly mediationLayerApi = inject(AwpMediationLayerApiService)
  private readonly route = inject(ActivatedRoute)

  readonly runs = signal<WorkflowRun[]>([])
  readonly loading = signal(false)
  readonly error = signal<string | null>(null)
  readonly lastRefresh = signal<Date | null>(null)
  readonly autoRefresh = signal(true)
  readonly statusFilter = signal<RunFilterKey>('all')
  readonly fractionSearch = signal('')
  readonly page = signal(0)
  readonly pageSize = 50
  readonly total = signal(0)
  readonly counts = signal<WorkflowRunCounts>({ ...EMPTY_COUNTS })

  /** Se valorizzato, lo storico è vincolato a un solo frazionario (scheda sede). */
  readonly frazFilter = input<string | null>(null)

  /** Se valorizzato, apre automaticamente il dettaglio della run indicata. */
  readonly focusRunId = input<string | null>(null)

  /**
   * Monitor incorporato (es. dialog "Monitor run" del dialog frazionario/site-hub).
   * In questo contesto il drill-down non usa overlay globali (drawer) — che lottano
   * sugli z-index con il dialog host — ma un pannello inline interno al monitor.
   */
  readonly embedded = input(false, { transform: booleanAttribute })

  private readonly frazFilterEffect = effect(() => {
    const fraz = String(this.frazFilter() ?? '').trim()
    if (!fraz || fraz === this.fractionSearch()) return
    this.fractionSearch.set(fraz)
    this.page.set(0)
    this.load()
  })

  private readonly focusRunEffect = effect(() => {
    const id = this.focusRunId()
    if (id) this.openRunById(id)
  })

  readonly formatDateTime = formatDateTime
  readonly formatTime = formatTime

  readonly tableId = 'mediation-runs'
  readonly tableColumns = [
    { field: 'run_name', header: 'Nome run', frozen: true },
    { field: 'state', header: 'Stato', sortable: true },
    { field: 'when', header: 'Avvio' },
    { field: 'fraz', header: 'Frazionari' },
    { field: 'ops', header: '', sortable: false }
  ]

  readonly filteredRuns = computed<WorkflowRun[]>(() => this.runs())

  readonly pageCount = computed(() =>
    Math.max(1, Math.ceil(this.total() / this.pageSize))
  )

  /** Frazionari distinti visti nello storico (accumulati tra le pagine). */
  private readonly knownFractions = signal<string[]>([])
  readonly allFractions = computed<string[]>(() => this.knownFractions())

  readonly fractionOptions = computed(() =>
    this.allFractions().map(value => ({ label: value, value }))
  )

  readonly hasFractionFilter = computed(() => !!this.fractionSearch().trim())

  clearFractionSearch (): void {
    this.fractionSearch.set('')
    this.page.set(0)
    this.load()
  }

  setFractionSearch (value: string | null): void {
    this.fractionSearch.set(value ?? '')
    this.page.set(0)
    this.load()
  }

  nextPage (): void {
    if ((this.page() + 1) * this.pageSize >= this.total()) return
    this.page.update(value => value + 1)
    this.load()
  }

  previousPage (): void {
    if (this.page() <= 0) return
    this.page.update(value => value - 1)
    this.load()
  }

  /** Salto diretto a una pagina, valido tra 0 e pageCount - 1. */
  goToPage (page: number): void {
    if (!Number.isFinite(page)) return
    const target = Math.min(Math.max(Math.trunc(page), 0), this.pageCount() - 1)
    if (target === this.page()) return
    this.page.set(target)
    this.load()
  }

  /** Azzera i filtri utente (unlock dello status; il frazionario bloccato da input resta). */
  resetFilters (): void {
    this.statusFilter.set('all')
    if (this.frazFilter()) {
      this.page.set(0)
      this.load()
      return
    }
    this.clearFractionSearch()
  }

  readonly activeFilterChips = computed(() => {
    const filter = this.statusFilter()
    if (filter === 'all') return []
    return [this.filterLabel(filter)]
  })

  private refreshSub: Subscription | null = null
  private detailSub: Subscription | null = null

  readonly detailOpen = signal(false)
  readonly detailRun = signal<WorkflowRun | null>(null)

  // ─────────────────────────── drill-down frazionari ───────────────────────────
  readonly drillOpen = signal(false)
  readonly selectedFraz = signal<string | null>(null)

  readonly drillRows = computed<FrazDrillRow[]>(() => {
    const run = this.detailRun()
    if (!run) return []

    const results = run.results ?? []
    const resultMap = new Map(results.map(result => [result.fraz, result]))
    const frazList = run.fraz?.length ? run.fraz : results.map(result => result.fraz)

    const rows: FrazDrillRow[] = []
    const seen = new Set<string>()
    for (const fraz of frazList) {
      if (seen.has(fraz)) continue
      seen.add(fraz)
      rows.push(this.toDrillRow(fraz, resultMap.get(fraz)?.steps ?? [], run.status))
    }
    for (const result of results) {
      if (seen.has(result.fraz)) continue
      seen.add(result.fraz)
      rows.push(this.toDrillRow(result.fraz, result.steps ?? [], run.status))
    }
    return rows
  })

  private toDrillRow (fraz: string, steps: WorkflowRunStepResult[], runStatus: string): FrazDrillRow {
    const successCount = steps.filter(step => step.status === 'success').length
    const failedCount = steps.filter(step => step.status === 'failed').length
    const skippedCount = steps.filter(step => step.status === 'skipped').length

    let status = runStatus
    if (steps.length) {
      if (failedCount) status = 'failed'
      else if (successCount === steps.length) status = 'completed'
      else if (successCount) status = 'running'
      else status = 'queued'
    }

    const failedStep = steps.find(step => step.status === 'failed')

    return {
      fraz,
      status,
      steps,
      successCount,
      failedCount,
      skippedCount,
      addedTags: [...new Set(steps.flatMap(step => step.addedTags ?? []))],
      removedTags: [...new Set(steps.flatMap(step => step.removedTags ?? []))],
      durationMs: steps.reduce((sum, step) => sum + (step.durationMs ?? 0), 0),
      error: failedStep?.message ?? null,
      failedStepLabel: failedStep?.stepLabel ?? null
    }
  }

  /** Numero di frazionari con almeno uno step fallito. */
  readonly failedFractionsCount = computed(() =>
    this.drillRows().filter(row => row.failedCount > 0).length
  )

  // ─────────────────────────────── vista grafo (storico run) ───────────────────────────────
  readonly detailView = signal<'details' | 'graph'>('details')
  readonly graphFraction = signal<string | null>(null)
  readonly graphSelectedNodeId = signal<string | null>(null)

  /** Scrub della history: indice nella timeline. MAX = segue l'ultimo step eseguito. */
  readonly timelineIndex = signal<number>(Number.MAX_SAFE_INTEGER)
  readonly timelinePlaying = signal(false)
  private timelineSub: Subscription | null = null

  /** Frazionario usato per colorare il grafo (default: il primo della run). */
  readonly activeGraphFraction = computed<string | null>(() => {
    const run = this.detailRun()
    if (!run) return null
    const chosen = this.graphFraction()
    if (chosen && (run.fraz ?? []).includes(chosen)) return chosen
    return run.fraz?.[0] ?? null
  })

  readonly graphFractionOptions = computed(() =>
    (this.detailRun()?.fraz ?? []).map(fraz => ({ label: fraz, value: fraz }))
  )

  /** Step eseguiti (in ordine di esecuzione) per il frazionario attivo. */
  readonly timelineSteps = computed<WorkflowRunStepResult[]>(() => {
    const run = this.detailRun()
    const fraz = this.activeGraphFraction()
    if (!run || !fraz) return []
    return run.results?.find(item => item.fraz === fraz)?.steps ?? []
  })

  readonly timelineLength = computed(() => this.timelineSteps().length)

  /** Indice effettivo della timeline, limitato al numero di step eseguiti. */
  readonly effectiveTimelineIndex = computed(() =>
    Math.max(0, Math.min(this.timelineIndex(), this.timelineLength()))
  )

  readonly timelineCurrentStep = computed<WorkflowRunStepResult | null>(() => {
    const index = this.effectiveTimelineIndex()
    if (index <= 0) return null
    return this.timelineSteps()[index - 1] ?? null
  })

  /** Posizione (rank) di ogni step nell'ordine di esecuzione, indicizzata per stepOrder. */
  private readonly graphExecutionRanks = computed<Map<string, number>>(() => {
    const map = new Map<string, number>()
    this.timelineSteps().forEach((step, index) => map.set(String(step.stepId), index))
    return map
  })

  /** Mappa stepOrder -> esito per il frazionario selezionato. */
  private readonly graphStatusMap = computed<Map<string, string>>(() => {
    const map = new Map<string, string>()
    this.timelineSteps().forEach(step => map.set(String(step.stepId), step.status))
    return map
  })

  readonly runGraphNodes = computed<WorkflowGraphNode[]>(() => {
    const run = this.detailRun()
    if (!run) return []
    const actions = run.actions ?? []
    const statusMap = this.graphStatusMap()
    const ranks = this.graphExecutionRanks()
    const resultMap = new Map(this.timelineSteps().map(step => [String(step.stepId), step]))
    const effective = this.effectiveTimelineIndex()
    const hasTimeline = this.timelineLength() > 0

    const nodeIdAt = (index: number): string => actions[index]?.step_id ?? `order:${index}`
    const buildTargets = (action: WorkflowRunActionPayload, index: number): { on: string; target: string }[] => {
      const transitions = action.transitions ?? []
      const catalogOutputs = actionOutputs(action.key)
      const runKeys = (action.outcomes ?? []).map(outcome => outcome.key)
      const keys = runKeys.length ? runKeys : catalogOutputs.map(output => output.key)
      const successKeys = new Set(catalogOutputs.filter(output => output.severity === 'success').map(output => output.key))
      if (!successKeys.size && keys.length) successKeys.add(keys[0])
      const result: { on: string; target: string }[] = []
      keys.forEach(on => {
        const transition = transitions.find(item => item.on === on)
        if (transition) {
          if (transition.target) result.push({ on, target: transition.target })
        } else if (successKeys.has(on) && index + 1 < actions.length) {
          // Fallback lineare: esito "success-like" senza transizione -> step successivo.
          result.push({ on, target: nodeIdAt(index + 1) })
        }
      })
      return result
    }

    return actions.map((action, index) => {
      const id = nodeIdAt(index)
      const rank = ranks.get(String(index))
      const executed = rank !== undefined && rank < effective
      const tagsCount = (action.outcomes ?? []).reduce(
        (sum, outcome) => sum + (outcome?.addTags?.length ?? 0) + (outcome?.removeTags?.length ?? 0), 0
      )
      const result = resultMap.get(String(index))
      return {
        id,
        order: index,
        label: action.name ?? action.type ?? action.key,
        subtitle: this.actionSubtitle(action.type ?? action.key),
        technology: this.actionTechnology(action.type ?? action.key),
        icon: this.actionIcon(action.key),
        actionKey: action.key,
        isEntry: run.entry_step_id ? id === run.entry_step_id : index === 0,
        targets: buildTargets(action, index),
        tagsCount,
        status: executed ? (result?.status ?? statusMap.get(String(index)) ?? null) : null,
        durationMs: executed ? result?.durationMs : undefined,
        message: executed && result?.status === 'failed' ? result.message : undefined,
        takenOutcome: executed ? (result?.outcome ?? null) : null,
        current: hasTimeline && effective > 0 && rank === effective - 1,
        dimmed: hasTimeline && !executed
      }
    })
  })

  readonly selectedGraphNode = computed<WorkflowGraphNode | null>(() => {
    const id = this.graphSelectedNodeId()
    if (!id) return null
    return this.runGraphNodes().find(node => node.id === id) ?? null
  })

  /** Esito dello step selezionato nel grafo per il frazionario attivo. */
  readonly selectedGraphResult = computed<WorkflowRunStepResult | null>(() => {
    const node = this.selectedGraphNode()
    if (!node) return null
    return this.timelineSteps().find(step => String(step.stepId) === String(node.order)) ?? null
  })

  setDetailView (view: 'details' | 'graph'): void {
    this.detailView.set(view)
    if (view === 'graph') {
      this.graphSelectedNodeId.set(null)
      this.attachTimelineToLive()
    } else {
      this.stopTimeline()
    }
  }

  selectGraphFraction (fraz: string | null): void {
    this.graphFraction.set(fraz)
    this.graphSelectedNodeId.set(null)
    this.attachTimelineToLive()
  }

  onGraphNodeSelect (nodeId: string): void {
    this.graphSelectedNodeId.set(this.graphSelectedNodeId() === nodeId ? null : nodeId)
  }

  get graphFractionValue (): string | null {
    return this.activeGraphFraction()
  }

  // ── timeline (scrub della history) ──
  attachTimelineToLive (): void {
    this.stopTimeline()
    this.timelineIndex.set(Number.MAX_SAFE_INTEGER)
  }

  onTimelineSeek (value: string): void {
    this.stopTimeline()
    this.timelineIndex.set(Number(value))
  }

  restartTimeline (): void {
    this.stopTimeline()
    this.timelineIndex.set(0)
    this.startTimeline()
  }

  toggleTimelinePlay (): void {
    if (this.timelinePlaying()) {
      this.stopTimeline()
      return
    }
    this.startTimeline()
  }

  private startTimeline (): void {
    const length = this.timelineLength()
    if (length <= 0) return
    if (this.effectiveTimelineIndex() >= length) this.timelineIndex.set(0)
    this.timelinePlaying.set(true)
    this.timelineSub?.unsubscribe()
    this.timelineSub = interval(700).subscribe(() => {
      const next = this.effectiveTimelineIndex() + 1
      if (next >= this.timelineLength()) {
        this.timelineIndex.set(this.timelineLength())
        this.stopTimeline()
        return
      }
      this.timelineIndex.set(next)
    })
  }

  private stopTimeline (): void {
    this.timelineSub?.unsubscribe()
    this.timelineSub = null
    this.timelinePlaying.set(false)
  }

  ngOnInit (): void {
    const lockedFraz = String(this.frazFilter() ?? '').trim()
    if (lockedFraz) {
      this.fractionSearch.set(lockedFraz)
    }
    this.load()
    const runId = lockedFraz ? null : this.route.snapshot.queryParamMap.get('run')
    if (runId) {
      this.openRunById(runId)
    }
    this.loadTags()
    this.loadActions()
    this.refreshSub = interval(10000).subscribe(() => {
      if (this.autoRefresh()) this.load(true)
    })
  }

  /** Icone azioni per i nodi del grafo. */
  readonly actionIcons = signal<Record<string, string>>({})

  private loadActions (): void {
    this.mediationWorkflowApi.getActions().subscribe({
      next: (actions) => {
        const usable = (actions ?? []).filter(action => action.hasExecutor !== false)
        setActionCatalog(usable)
        const map: Record<string, string> = {}
        usable.forEach(action => { map[action.key] = action.icon })
        this.actionIcons.set(map)
      },
      error: () => { /* fallback icona generica */ }
    })
  }

  actionIcon (key: string): string {
    return this.actionIcons()[key] ?? 'fa-solid fa-gear'
  }

  private actionTechnology (type: string): 'fortinet' | 'cisco' | 'nrd' | 'generic' {
    const key = String(type || '').toLowerCase()
    if (key.includes('fortinet')) return 'fortinet'
    if (key.includes('cisco')) return 'cisco'
    if (key.includes('nrd')) return 'nrd'
    return 'generic'
  }

  private actionSubtitle (type: string): string {
    const labels: Record<string, string> = {
      'sdwan-fortinet': 'Fortinet · FortiManager',
      'sdwan-cisco': 'Cisco · vManage',
      'ztp-nrd': 'ZTP NRD',
      nrd: 'NRD',
      'firmware-upgrade': 'Generico · Firmware',
      'assign-vlan': 'Generico · VLAN',
      'reboot-device': 'Generico · Apparato',
      'connectivity-check': 'Generico · Connettività',
      custom: 'Generico · Custom',
      'bulk-custom': 'Generico · Batch'
    }
    return labels[type] || type || 'Azione'
  }

  /** Anagrafica tag per risolvere gli id nelle esecuzioni (add/remove). */
  readonly tags = signal<MediationTag[]>([])

  private loadTags (): void {
    this.mediationLayerApi.getTags().subscribe({
      next: (tags) => this.tags.set(tags ?? []),
      error: () => { /* i tag restano vuoti: si mostra l'id */ }
    })
  }

  tagName (tagId: number): string {
    const tag = this.tags().find(item => Number(item.id) === Number(tagId))
    return tag?.name ?? `Tag ${tagId}`
  }

  ngOnDestroy (): void {
    this.refreshSub?.unsubscribe()
    this.detailSub?.unsubscribe()
    this.stopTimeline()
  }

  load (silent = false): void {
    if (!silent) this.loading.set(true)
    const status = this.statusFilter() === 'all' ? undefined : this.statusFilter()
    this.mediationWorkflowApi.getRunsPage({
      limit: this.pageSize,
      offset: this.page() * this.pageSize,
      fraz: this.fractionSearch().trim() || undefined,
      status
    }).subscribe({
      next: (page: any) => {
        const items: WorkflowRun[] = Array.isArray(page) ? page : (page?.items ?? [])
        this.runs.set(items)
        this.total.set(page?.total ?? items.length)
        // Il backend può non restituire `counts` (es. versioni precedenti o risposte parziali):
        // senza il fallback il template esplode su `counts().all`.
        const counts: WorkflowRunCounts = { ...EMPTY_COUNTS, ...(page?.counts ?? {}) }
        if (!page?.counts) {
          counts.all = this.total()
        }
        this.counts.set(counts)
        const seen = new Set(this.knownFractions())
        items.forEach(run => (run.fraz ?? []).forEach(fraz => seen.add(String(fraz))))
        this.knownFractions.set([...seen].sort((a, b) => a.localeCompare(b)))
        this.error.set(null)
        this.loading.set(false)
        this.lastRefresh.set(new Date())
      },
      error: () => {
        this.error.set('Impossibile caricare le run')
        this.loading.set(false)
      }
    })
  }

  setFilter (filter: RunFilterKey): void {
    this.statusFilter.set(filter)
    this.page.set(0)
    this.load()
  }

  toggleAutoRefresh (): void {
    this.autoRefresh.update(value => !value)
  }

  filterLabel (filter: RunFilterKey): string {
    const labels: Record<RunFilterKey, string> = {
      all: 'Tutte',
      active: 'In corso',
      scheduled: 'Pianificate',
      waiting: 'In attesa',
      completed: 'Completate',
      failed: 'Fallite'
    }
    return labels[filter]
  }

  isRunActive (status: string): boolean {
    return status === 'queued' || status === 'scheduled' || status === 'waiting' || status === 'running'
  }

  isRunTerminal (status: string): boolean {
    return status === 'completed' || status === 'failed' || status === 'cancelled'
  }

  runWhen (run: WorkflowRun): string {
    if (run.execution_mode === 'scheduled' && run.scheduled_at) {
      return `Pianificata · ${formatDateTime(run.scheduled_at)}`
    }
    return run.createdAt ? `Immediata · ${formatDateTime(run.createdAt)}` : '-'
  }

  executionModeLabel (run: WorkflowRun): string {
    return run.execution_mode === 'scheduled' ? 'Pianificata' : 'Immediata'
  }

  formatDuration (durationMs?: number): string {
    if (!durationMs || durationMs <= 0) return '-'
    if (durationMs < 1000) return `${durationMs} ms`
    const seconds = durationMs / 1000
    if (seconds < 60) return `${seconds.toFixed(1)} s`
    const minutes = Math.floor(seconds / 60)
    const remaining = Math.round(seconds % 60)
    return `${minutes} m ${remaining} s`
  }

  statusLabel (status: string): string {
    const labels: Record<string, string> = {
      queued: 'In coda',
      scheduled: 'Pianificata',
      waiting: 'In attesa',
      running: 'In esecuzione',
      completed: 'Completata',
      failed: 'Fallita',
      cancelled: 'Annullata',
      success: 'Successo',
      neutral: 'Neutrale',
      skipped: 'Saltata'
    }
    return labels[status] ?? status
  }

  /** Icona di stato, usata dove la pill deve essere leggibile a colpo d'occhio. */
  statusIcon (status: string): string {
    const icons: Record<string, string> = {
      queued: 'fa-solid fa-hourglass-start',
      scheduled: 'fa-solid fa-clock',
      waiting: 'fa-solid fa-hourglass-half',
      running: 'fa-solid fa-rotate',
      completed: 'fa-solid fa-circle-check',
      failed: 'fa-solid fa-circle-xmark',
      cancelled: 'fa-solid fa-ban',
      success: 'fa-solid fa-circle-check',
      neutral: 'fa-solid fa-circle',
      skipped: 'fa-solid fa-circle-minus'
    }
    return icons[status] ?? 'fa-solid fa-circle'
  }

  /**
   * Avanzamento live di una run, ricostruito dai risultati già noti alla lista.
   * Le run senza risultati caricati (lista appena arrivarata) restano senza barra.
   */
  runProgress (run: WorkflowRun): { done: number; total: number; running: boolean } | null {
    if (!this.isRunActive(run.status)) return null
    const steps = (run.results ?? []).flatMap(result => result.steps ?? [])
    if (!steps.length) return null
    const done = steps.filter(step => step.status !== 'running').length
    return { done, total: steps.length, running: true }
  }

  /** Numero di frazionari con almeno uno step fallito, se i risultati sono noti. */
  runFailedFrazCount (run: WorkflowRun): number {
    if (run.status !== 'failed') return 0
    const results = run.results ?? []
    if (!results.length) return 0
    return results.filter(result => (result.steps ?? []).some(step => step.status === 'failed')).length
  }

  statusKey (status: string): string {
    return String(status ?? '').trim().toLowerCase()
  }

  outcomeSeverity (outcomeKey: string): string {
    const key = this.statusKey(outcomeKey)
    if (key === 'failed' || key === 'error' || key === 'ko') return 'failed'
    if (key === 'success' || key === 'ok' || key === 'completed') return 'success'
    return 'warning'
  }

  outcomeIcon (outcomeKey: string): string {
    const severity = this.outcomeSeverity(outcomeKey)
    if (severity === 'failed') return 'fa-solid fa-circle-xmark'
    if (severity === 'warning') return 'fa-solid fa-triangle-exclamation'
    return 'fa-solid fa-circle-check'
  }

  outcomeSeverityOf (outcome: WorkflowRunActionOutcome): string {
    return outcome.severity ?? this.outcomeSeverity(outcome.key)
  }

  outcomeIconOf (outcome: WorkflowRunActionOutcome): string {
    if (outcome.severity === 'failed') return 'fa-solid fa-circle-xmark'
    if (outcome.severity === 'warning') return 'fa-solid fa-triangle-exclamation'
    if (outcome.severity === 'neutral') return 'fa-solid fa-circle-minus'
    if (outcome.severity === 'success') return 'fa-solid fa-circle-check'
    return this.outcomeIcon(outcome.key)
  }

  outcomeLabelOf (outcome: WorkflowRunActionOutcome): string {
    return outcome.label ?? outcome.key
  }

  /** Motivo del fallimento di un frazionario, ricostruito dal primo step fallito. */
  resultFailureMessage (result: WorkflowRunResult): string | null {
    const failed = result.steps?.find(step => step.status === 'failed')
    if (!failed) return null
    return failed.message ? `${failed.stepLabel}: ${failed.message}` : failed.stepLabel
  }

  // ─────────────────────────────── drill-down frazionari ───────────────────────────────
  openFrazDrill (run: WorkflowRun, event?: Event): void {
    event?.stopPropagation()
    this.detailRun.set(run)
    // Embedded: il dialog "Dettaglio run" lascerebbe il pannello inline coperto — chiudilo.
    if (this.embedded()) this.detailOpen.set(false)
    this.detailView.set('details')
    this.graphFraction.set(null)
    this.graphSelectedNodeId.set(null)
    this.attachTimelineToLive()
    this.selectedFraz.set(run.fraz?.[0] ?? run.results?.[0]?.fraz ?? null)
    this.drillOpen.set(true)
    this.loadRunDetail(run.id)
    this.startDetailStream(run.id)
  }

  closeFrazDrill (): void {
    this.detailSub?.unsubscribe()
    this.detailSub = null
    this.stopTimeline()
    this.drillOpen.set(false)
    this.selectedFraz.set(null)
    this.detailRun.set(null)
  }

  /** Esc: chiude il pannello frazionari (non modale, in entrambi i contesti). */
  onDocumentEscapeKey (): void {
    if (this.drillOpen()) this.closeFrazDrill()
  }

  selectFraz (fraz: string): void {
    this.selectedFraz.set(this.selectedFraz() === fraz ? null : fraz)
  }

  isFrazSelected (fraz: string): boolean {
    return this.selectedFraz() === fraz
  }

  // ─────────────────────────────── azioni ───────────────────────────────
  private openRunById (id: string): void {
    this.mediationWorkflowApi.getRun(id).subscribe({
      next: (run) => { if (run) this.openDetail(run) }
    })
  }

  openDetail (run: WorkflowRun): void {
    this.drillOpen.set(false)
    this.selectedFraz.set(null)
    this.detailRun.set(run)
    this.detailOpen.set(true)
    this.detailView.set('details')
    this.graphFraction.set(null)
    this.graphSelectedNodeId.set(null)
    this.attachTimelineToLive()
    this.loadRunDetail(run.id)
    this.startDetailStream(run.id)
  }

  closeDetail (): void {
    this.detailSub?.unsubscribe()
    this.detailSub = null
    this.stopTimeline()
    this.detailOpen.set(false)
    this.detailRun.set(null)
  }

  /**
   * Carica la run completa (azioni + esiti). Necessario perché la lista run
   * non include i risultati: senza questa fetch il drill-down resterebbe vuoto.
   */
  private loadRunDetail (runId: string): void {
    this.mediationWorkflowApi.getRun(runId).subscribe({
      next: (full) => {
        if (this.detailRun()?.id === full.id) this.detailRun.set(full)
        this.runs.update(list => list.map(item => item.id === full.id
          ? { ...item, status: full.status, results: full.results, error: full.error }
          : item))
      }
    })
  }

  private startDetailStream (runId: string): void {
    this.detailSub?.unsubscribe()
    this.detailSub = this.mediationWorkflowApi.streamRun(runId).subscribe({
      next: (payload) => this.applyRunStream(runId, payload),
      error: () => this.startDetailPolling(runId),
      complete: () => this.load(true)
    })
  }

  private applyRunStream (runId: string, payload: any): void {
    const current = this.detailRun()
    if (!current || current.id !== runId) return
    const updated = {
      ...current,
      status: payload.status ?? current.status,
      startedAt: payload.startedAt ?? current.startedAt,
      finishedAt: payload.finishedAt ?? current.finishedAt,
      results: payload.results ?? current.results
    }
    this.detailRun.set(updated)
    this.runs.update(list => list.map(item => item.id === runId
      ? { ...item, status: updated.status, results: updated.results }
      : item))
  }

  private startDetailPolling (runId: string): void {
    this.detailSub?.unsubscribe()
    this.detailSub = interval(2500).subscribe(() => {
      const current = this.detailRun()
      if (!current || current.id !== runId || !this.isRunActive(current.status)) return
      this.loadRunDetail(runId)
    })
  }

  refreshDetail (): void {
    const run = this.detailRun()
    if (run) this.loadRunDetail(run.id)
  }

  cancelRun (run: WorkflowRun, event: Event): void {
    event.stopPropagation()
    this.mediationWorkflowApi.cancelRun(run.id).subscribe({
      next: () => {
        const running = run.status === 'running'
        this.messageService.add({
          severity: 'success',
          summary: running ? 'Annullamento richiesto' : 'Run annullata',
          detail: running ? 'La run si fermerà al termine dello step corrente.' : run.run_name
        })
        this.load(true)
      },
      error: (error) => this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: error?.error?.error ?? 'Impossibile annullare la run'
      })
    })
  }

  retryRun (run: WorkflowRun, event: Event): void {
    event.stopPropagation()
    this.mediationWorkflowApi.retryRun(run.id).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Retry falliti avviato', detail: run.run_name })
        this.load(true)
      },
      error: (error) => this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: error?.error?.error ?? 'Impossibile avviare il retry'
      })
    })
  }

  deleteRun (run: WorkflowRun, event: Event): void {
    event.stopPropagation()
    this.mediationWorkflowApi.deleteRun(run.id).subscribe({
      next: () => {
        if (this.detailRun()?.id === run.id) {
          if (this.drillOpen()) this.closeFrazDrill()
          if (this.detailOpen()) this.closeDetail()
        }
        this.runs.update(list => list.filter(item => item.id !== run.id))
        this.messageService.add({ severity: 'success', summary: 'Run eliminata', detail: run.run_name })
      },
      error: (error) => this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: error?.error?.error ?? 'Impossibile eliminare la run'
      })
    })
  }

  postActionSummary (post: { key: string; emails?: string[] }): string {
    return post.key === 'send-email' && post.emails?.length
      ? `send-email (${post.emails.join(', ')})`
      : post.key
  }

  paramsEntries (action: WorkflowRunActionPayload): { key: string; value: unknown }[] {
    return Object.entries(action.params ?? {}).map(([key, value]) => ({ key, value }))
  }
}
